// Patrimoine (M10, lot L4-A, H5-03, D-144) : inventaire unique des biens de la collectivité (les marchés
// municipaux et les équipements réservables en sont), référence BIEN attribuée par la numérotation,
// catégories, statuts juridiques, responsable, affectations successives (service, agent ou lieu),
// opérations de maintenance avec coûts et pièces, inventaires périodiques avec écarts, sortie du
// patrimoine en deux temps (demande motivée, validation par le maire ou président avec pièce) ; un bien
// sorti reste consultable. Montants en francs CFA, entiers.
import { z } from 'zod';
import { schemaBbox } from './dossiers';
import { schemaPosition } from './marches-structure';
import { schemaJourCivil } from './proprete';

const uuid = z.string().uuid('Identifiant invalide');
const cles = <T extends Record<string, string>>(o: T) => Object.keys(o) as [keyof T & string, ...(keyof T & string)[]];

export const CATEGORIES_BIEN = {
  batiment: 'Bâtiment',
  salle: 'Salle',
  terrain: 'Terrain',
  equipement_sportif: 'Équipement sportif',
  marche: 'Marché municipal',
  vehicule: 'Véhicule',
  materiel: 'Matériel technique',
  mobilier: 'Mobilier',
  informatique: 'Informatique',
  voirie: 'Voirie et réseaux',
  autre: 'Autre',
} as const;
export type CategorieBien = keyof typeof CATEGORIES_BIEN;
export const CODES_CATEGORIES_BIEN = cles(CATEGORIES_BIEN);

export const STATUTS_JURIDIQUES_BIEN = {
  domaine_public: 'Domaine public',
  domaine_prive: 'Domaine privé',
  mis_a_disposition: 'Mis à disposition',
  location: 'Pris en location',
  a_determiner: 'À déterminer',
} as const;
export type StatutJuridiqueBien = keyof typeof STATUTS_JURIDIQUES_BIEN;
export const CODES_STATUTS_JURIDIQUES = cles(STATUTS_JURIDIQUES_BIEN);

/** Cycle de vie : sortie_demandee et sorti sont posés par la procédure de sortie, jamais par une modification. */
export const ETATS_BIEN = {
  en_service: 'En service',
  en_maintenance: 'En maintenance',
  hors_service: 'Hors service',
  sortie_demandee: 'Sortie demandée',
  sorti: 'Sorti du patrimoine',
} as const;
export type EtatBien = keyof typeof ETATS_BIEN;
export const CODES_ETATS_BIEN = cles(ETATS_BIEN);
export const ETATS_BIEN_MODIFIABLES = ['en_service', 'en_maintenance', 'hors_service'] as const;

export const CONDITIONS_BIEN = { neuf: 'Neuf', bon: 'Bon', moyen: 'Moyen', mauvais: 'Mauvais' } as const;
export type ConditionBien = keyof typeof CONDITIONS_BIEN;
export const CODES_CONDITIONS_BIEN = cles(CONDITIONS_BIEN);

export const NATURES_MAINTENANCE = {
  preventive: 'Préventive',
  corrective: 'Corrective',
  reparation: 'Réparation',
  controle_reglementaire: 'Contrôle réglementaire',
} as const;
export type NatureMaintenance = keyof typeof NATURES_MAINTENANCE;
export const CODES_NATURES_MAINTENANCE = cles(NATURES_MAINTENANCE);

export const ETATS_MAINTENANCE = { planifiee: 'Planifiée', realisee: 'Réalisée', annulee: 'Annulée' } as const;
export type EtatMaintenance = keyof typeof ETATS_MAINTENANCE;
export const CODES_ETATS_MAINTENANCE = cles(ETATS_MAINTENANCE);

export const ETATS_INVENTAIRE = { en_cours: 'En cours', clos: 'Clos' } as const;
export type EtatInventaire = keyof typeof ETATS_INVENTAIRE;

/** Constat d'une ligne d'inventaire : présent sans écart, ou un écart (absent, déplacé, endommagé). */
export const CONSTATS_INVENTAIRE = { present: 'Présent', absent: 'Absent', deplace: 'Déplacé', endommage: 'Endommagé' } as const;
export type ConstatInventaire = keyof typeof CONSTATS_INVENTAIRE;
export const CODES_CONSTATS_INVENTAIRE = cles(CONSTATS_INVENTAIRE);

export const MODES_SORTIE = {
  reforme: 'Réforme',
  cession: 'Cession',
  destruction: 'Destruction',
  perte: 'Perte',
  vol: 'Vol',
  transfert: 'Transfert à une autre entité',
} as const;
export type ModeSortie = keyof typeof MODES_SORTIE;
export const CODES_MODES_SORTIE = cles(MODES_SORTIE);

export const ETATS_SORTIE = { demandee: 'À valider', validee: 'Validée', refusee: 'Refusée' } as const;
export type EtatSortie = keyof typeof ETATS_SORTIE;
export const CODES_ETATS_SORTIE = cles(ETATS_SORTIE);

const texte = (min: number, max: number) => z.string().trim().min(min).max(max);
const montant = z.number().int('Montant entier en francs CFA').nonnegative().max(1_000_000_000_000);

// Biens ----------------------------------------------------------------------------------------

export const schemaCreationBien = z.strictObject({
  categorie: z.enum(CODES_CATEGORIES_BIEN),
  designation: texte(2, 200),
  description: z.string().trim().max(2000).optional(),
  statut_juridique: z.enum(CODES_STATUTS_JURIDIQUES).default('a_determiner'),
  localisation: z.string().trim().max(300).optional(),
  arrondissement_id: uuid.optional(),
  position: schemaPosition.optional(),
  responsable_appartenance_id: uuid.optional(),
  valeur_acquisition: montant.optional(),
  date_acquisition: schemaJourCivil.optional(),
  mode_acquisition: z.string().trim().max(120).optional(),
  condition: z.enum(CODES_CONDITIONS_BIEN).optional(),
  /** Périodicité de l'inventaire physique, en mois (D-161). */
  periodicite_inventaire_mois: z.number().int().min(1).max(120).default(12),
});
export type CreationBien = z.infer<typeof schemaCreationBien>;

export const schemaModificationBien = z.strictObject({
  designation: texte(2, 200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  statut_juridique: z.enum(CODES_STATUTS_JURIDIQUES).optional(),
  localisation: z.string().trim().max(300).nullable().optional(),
  arrondissement_id: uuid.nullable().optional(),
  position: schemaPosition.nullable().optional(),
  responsable_appartenance_id: uuid.nullable().optional(),
  valeur_acquisition: montant.nullable().optional(),
  date_acquisition: schemaJourCivil.nullable().optional(),
  mode_acquisition: z.string().trim().max(120).nullable().optional(),
  condition: z.enum(CODES_CONDITIONS_BIEN).nullable().optional(),
  periodicite_inventaire_mois: z.number().int().min(1).max(120).optional(),
  etat: z.enum(ETATS_BIEN_MODIFIABLES).optional(),
});

export const schemaFiltreBiens = z.strictObject({
  q: z.string().trim().max(100).optional(),
  categorie: z.enum(CODES_CATEGORIES_BIEN).optional(),
  etat: z.enum(CODES_ETATS_BIEN).optional(),
  statut_juridique: z.enum(CODES_STATUTS_JURIDIQUES).optional(),
  arrondissement_id: uuid.optional(),
  /** sans : biens sans responsable désigné (PAT-01). */
  responsable: z.enum(['avec', 'sans']).optional(),
  /** oui : inventaire périodique échu à ce jour (PAT-01). */
  inventaire_echu: z.enum(['oui']).optional(),
  reservable: z.enum(['oui', 'non']).optional(),
  /** Les biens sortis sont exclus par défaut ; inclure : oui. */
  sortis: z.enum(['oui', 'non']).default('non'),
  limite: z.coerce.number().int().min(1).max(1000).default(200),
});

export const schemaFiltreCarteBiens = z.strictObject({ bbox: schemaBbox.optional(), categorie: z.enum(CODES_CATEGORIES_BIEN).optional() });

// Affectations, maintenance ------------------------------------------------------------------------

export const schemaCreationAffectation = z
  .strictObject({
    service_id: uuid.optional(),
    appartenance_id: uuid.optional(),
    lieu: z.string().trim().max(300).optional(),
    debut: schemaJourCivil,
    motif: z.string().trim().max(500).optional(),
  })
  .refine((v) => Boolean(v.service_id || v.appartenance_id || v.lieu), { message: 'Une affectation désigne un service, un agent ou un lieu', path: ['service_id'] });

export const schemaCreationMaintenance = z.strictObject({
  nature: z.enum(CODES_NATURES_MAINTENANCE),
  objet: texte(3, 300),
  intervenant: z.string().trim().max(200).optional(),
  date_prevue: schemaJourCivil,
  cout_estime: montant.optional(),
  observations: z.string().trim().max(2000).optional(),
});

export const schemaRealisationMaintenance = z.strictObject({
  date_realisation: schemaJourCivil,
  cout: montant,
  observations: z.string().trim().max(2000).optional(),
  /** Condition du bien constatée après l'opération. */
  condition: z.enum(CODES_CONDITIONS_BIEN).optional(),
});

export const schemaAnnulationMaintenance = z.strictObject({ motif: texte(3, 500) });

// Inventaires périodiques -------------------------------------------------------------------------

export const schemaCreationInventaire = z.strictObject({
  libelle: texte(3, 200),
  categorie: z.enum(CODES_CATEGORIES_BIEN).optional(),
  arrondissement_id: uuid.optional(),
  date_debut: schemaJourCivil,
  responsable_appartenance_id: uuid.optional(),
});

export const schemaVerificationLigne = z
  .strictObject({
    constat: z.enum(CODES_CONSTATS_INVENTAIRE),
    condition: z.enum(CODES_CONDITIONS_BIEN).optional(),
    localisation_constatee: z.string().trim().max(300).optional(),
    observation: z.string().trim().max(1000).optional(),
  })
  .refine((v) => v.constat === 'present' || Boolean(v.observation?.trim()), { message: 'Un écart (absent, déplacé, endommagé) est expliqué', path: ['observation'] })
  .refine((v) => v.constat !== 'deplace' || Boolean(v.localisation_constatee?.trim()), { message: 'Indiquer où le bien a été trouvé', path: ['localisation_constatee'] });

// Sorties -------------------------------------------------------------------------------------------

export const schemaDemandeSortie = z.strictObject({
  mode: z.enum(CODES_MODES_SORTIE),
  motif: texte(10, 2000),
  date_sortie: schemaJourCivil.optional(),
});

export const schemaDecisionSortie = z
  .strictObject({ decision: z.enum(['valider', 'refuser']), motif: z.string().trim().max(2000).optional(), date_sortie: schemaJourCivil.optional() })
  .refine((v) => v.decision === 'valider' || (v.motif?.trim().length ?? 0) >= 3, { message: 'Un refus est motivé', path: ['motif'] });

export const schemaFiltreSorties = z.strictObject({ etat: z.enum(CODES_ETATS_SORTIE).optional(), limite: z.coerce.number().int().min(1).max(500).default(100) });

export const TYPES_DOCUMENT_BIEN = { photo: 'Photo', piece: 'Pièce' } as const;
export const schemaDocumentBien = z.strictObject({ type: z.enum(['photo', 'piece']).default('piece'), titre: z.string().trim().min(2).max(200).optional() });

// Réponses ----------------------------------------------------------------------------------------

export interface PersonneDesignee {
  appartenance_id: string;
  nom: string | null;
}

export interface Bien {
  id: string;
  reference: string;
  categorie: CategorieBien;
  categorie_libelle: string;
  designation: string;
  description: string | null;
  statut_juridique: StatutJuridiqueBien;
  statut_juridique_libelle: string;
  localisation: string | null;
  arrondissement: { id: string; nom: string } | null;
  position: { longitude: number; latitude: number } | null;
  responsable: PersonneDesignee | null;
  affectation: { service: string | null; agent: string | null; lieu: string | null; debut: string } | null;
  valeur_acquisition: number | null;
  date_acquisition: string | null;
  mode_acquisition: string | null;
  etat: EtatBien;
  etat_libelle: string;
  condition: ConditionBien | null;
  reservable: boolean;
  periodicite_inventaire_mois: number;
  dernier_inventaire_le: string | null;
  inventaire_echu: boolean;
  marche_id: string | null;
  sorti_le: string | null;
}

export interface AffectationBien {
  id: string;
  service: { id: string; nom: string } | null;
  agent: PersonneDesignee | null;
  lieu: string | null;
  debut: string;
  fin: string | null;
  motif: string | null;
}

export interface OperationMaintenance {
  id: string;
  bien_id: string;
  nature: NatureMaintenance;
  nature_libelle: string;
  objet: string;
  intervenant: string | null;
  date_prevue: string;
  date_realisation: string | null;
  cout_estime: number | null;
  cout: number | null;
  etat: EtatMaintenance;
  etat_libelle: string;
  observations: string | null;
  pieces: number;
}

export interface DocumentBien {
  id: string;
  titre: string;
  type_document: string;
  type_mime: string;
  taille: number;
  objet_type: string;
  cree_le: string;
}

export interface SortieBien {
  id: string;
  bien: { id: string; reference: string; designation: string; categorie: CategorieBien };
  mode: ModeSortie;
  mode_libelle: string;
  motif: string;
  etat: EtatSortie;
  etat_libelle: string;
  demandee_par: string | null;
  demandee_le: string;
  decision_par: string | null;
  decision_le: string | null;
  decision_motif: string | null;
  piece_document_id: string | null;
  date_sortie: string | null;
}

export interface BienDetail extends Bien {
  affectations: AffectationBien[];
  maintenances: OperationMaintenance[];
  cout_maintenance_total: number;
  inventaires: { inventaire_id: string; libelle: string; constat: ConstatInventaire | null; verifie_le: string | null }[];
  documents: DocumentBien[];
  sorties: SortieBien[];
  equipement: { id: string; code: string } | null;
}

export interface LigneInventaire {
  id: string;
  bien: { id: string; reference: string; designation: string; categorie: CategorieBien; localisation: string | null };
  constat: ConstatInventaire | null;
  condition: ConditionBien | null;
  localisation_constatee: string | null;
  observation: string | null;
  verifie_le: string | null;
  verifie_par: string | null;
}

export interface AvancementInventaire {
  total: number;
  verifiees: number;
  ecarts: number;
  /** Pourcentage de lignes vérifiées, une décimale. */
  taux: number;
}

export interface InventairePeriodique {
  id: string;
  libelle: string;
  categorie: CategorieBien | null;
  arrondissement: { id: string; nom: string } | null;
  date_debut: string;
  clos_le: string | null;
  etat: EtatInventaire;
  responsable: PersonneDesignee | null;
  avancement: AvancementInventaire;
}

export interface InventaireDetail extends InventairePeriodique {
  lignes: LigneInventaire[];
}

// Règles pures ------------------------------------------------------------------------------------

/** Une ligne vérifiée est un écart dès que le bien n'est pas trouvé présent. */
export function estEcart(l: Pick<LigneInventaire, 'constat'>): boolean {
  return l.constat !== null && l.constat !== 'present';
}

/** Avancement d'un inventaire : lignes vérifiées, écarts et taux de vérification (une décimale). */
export function avancementInventaire(lignes: readonly Pick<LigneInventaire, 'constat'>[]): AvancementInventaire {
  const total = lignes.length;
  const verifiees = lignes.filter((l) => l.constat !== null).length;
  const ecarts = lignes.filter(estEcart).length;
  return { total, verifiees, ecarts, taux: total === 0 ? 0 : Math.round((1000 * verifiees) / total) / 10 };
}

/** Ajoute n mois à un jour civil (jour borné à la fin du mois d'arrivée). */
export function ajouterMoisJour(jour: string, n: number): string {
  const [a, m, j] = jour.split('-').map(Number) as [number, number, number];
  const dernier = new Date(Date.UTC(a, m - 1 + n + 1, 0)).getUTCDate();
  return new Date(Date.UTC(a, m - 1 + n, Math.min(j, dernier))).toISOString().slice(0, 10);
}

/**
 * Inventaire périodique échu (PAT-01, D-161) : jamais inventorié depuis son entrée au patrimoine plus la
 * périodicité, ou dernier inventaire plus la périodicité antérieur à la date de référence. Un bien sorti n'est jamais échu.
 */
export function inventaireEchu(b: { dernier_inventaire_le: string | null; entree_le: string; periodicite_inventaire_mois: number; etat: EtatBien }, reference: string): boolean {
  if (b.etat === 'sorti') return false;
  return ajouterMoisJour(b.dernier_inventaire_le ?? b.entree_le, b.periodicite_inventaire_mois) < reference;
}
